import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import {
  UserListHeader,
  UserStats,
  UserFilters,
  UserTable,
  UserListMobile
} from '../components/UserList';
import { filterUsers } from '../components/UserList/UserListUtils';
import userService from '../services/userService';
import { getApiBaseUrl, safeParseJson } from '../utils/apiBaseUrl';
import { config } from '../env.js';


// Enhanced toast notification component
const Toast = ({ message, type = 'info', onClose, theme, currentThemeName }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const toastConfig = {
    success: {
      icon: CheckCircle,
      bg: currentThemeName === 'dark' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)',
      border: 'rgba(16, 185, 129, 0.3)',
      color: '#10b981',
      textColor: currentThemeName === 'dark' ? '#d1fae5' : '#065f46'
    },
    error: {
      icon: XCircle,
      bg: currentThemeName === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
      border: 'rgba(239, 68, 68, 0.3)',
      color: '#ef4444',
      textColor: currentThemeName === 'dark' ? '#fecaca' : '#991b1b'
    },
    warning: {
      icon: AlertTriangle,
      bg: currentThemeName === 'dark' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)',
      border: 'rgba(245, 158, 11, 0.3)',
      color: '#f59e0b',
      textColor: currentThemeName === 'dark' ? '#fef3c7' : '#92400e'
    },
    info: {
      icon: AlertTriangle,
      bg: currentThemeName === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
      border: 'rgba(59, 130, 246, 0.3)',
      color: '#3b82f6',
      textColor: currentThemeName === 'dark' ? '#dbeafe' : '#1e40af'
    }
  };

  const config = toastConfig[type];
  const Icon = config.icon;

  return (
    <div
      className="toast-notification"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        background: config.bg,
        border: `1px solid ${config.border}`,
        borderRadius: 12,
        padding: '16px 20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        zIndex: 10000,
        maxWidth: 400,
        backdropFilter: 'blur(10px)',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}
    >
      <Icon size={20} className="toast-icon" style={{ color: config.color, flexShrink: 0 }} />
      <div className="toast-content" style={{ flex: 1 }}>
        <div className="toast-title" style={{ 
          fontSize: 14, 
          fontWeight: 600, 
          color: config.textColor,
          marginBottom: 2
        }}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </div>
        <div className="toast-message" style={{ 
          fontSize: 13, 
          color: config.textColor,
          opacity: 0.9
        }}>
          {message}
        </div>
      </div>
      <button
        className="toast-close-button"
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }}
        style={{
          background: 'none',
          border: 'none',
          color: config.color,
          cursor: 'pointer',
          padding: 4,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <XCircle size={16} />
      </button>
    </div>
  );
};

function UserListPage() {
  const { theme, currentThemeName } = useTheme();
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    blocked: 0,
    inactive: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);

  const itemsPerPage = 10;

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out;
      }
      .animate-slide-in-right {
        animation: slideInRight 0.6s ease-out;
      }
      .animate-spin {
        animation: spin 1s linear infinite;
      }
      .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch users from mock service
  const fetchUsers = useCallback(async () => {
    try {
      if (!loading) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        status: statusFilter,
        search: searchTerm
      });

      const response = await fetch(`${getApiBaseUrl()}/api/auth/users?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication expired. Please login again.');
        }
        if (response.status === 403) {
          throw new Error('Access denied. Admin privileges required.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await safeParseJson(response);
      if (!data) {
        setUsers([]);
        setTotalUsers(0);
        setTotalPages(1);
        setError('Could not load user list. Check that the API is reachable.');
        return;
      }
      if (data.success) {
        setUsers(data.users || []);
        setTotalUsers(data.total ?? 0);
        setTotalPages(data.totalPages ?? 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      const errorMessage = err.message || err.toString() || 'Failed to load user data';
      setError(errorMessage);
      showToast(`Failed to load user data: ${errorMessage}`, 'error');
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, statusFilter, searchTerm, loading]);

  // Fetch user statistics from API
  const fetchUserStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${getApiBaseUrl()}/api/auth/users/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await safeParseJson(response);
      const stats = (data && data.success && data.stats) ? data.stats : {};
      
      setUserStats({
        total: stats.total || 0,
        active: stats.active || 0,
        pending: stats.pending || 0,
        blocked: stats.blocked || 0,
        inactive: stats.inactive || 0,
        admin: stats.admin || 0,
        newUsersThisWeek: stats.newUsersThisWeek || 0
      });
    } catch (err) {
      console.error('Error fetching user stats:', err);
      const errorMessage = err.message || err.toString() || 'Failed to fetch user statistics';
      showToast(`Failed to load user statistics: ${errorMessage}`, 'error');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  // Handle user action (approve, block, etc.)
  const handleUserAction = async (userId, action) => {
    // Show confirmation for delete action
    if (action === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to permanently delete this user? This action cannot be undone.`
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      let response;
      
      // Handle admin status changes differently
      if (action === 'setAdmin' || action === 'removeAdmin') {
        const isAdmin = action === 'setAdmin';
        const requestBody = { isAdmin };
        
        console.log('🔄 Admin action request:', {
          action,
          userId,
          isAdmin,
          requestBody,
          apiUrl: `${getApiBaseUrl()}/api/auth/users/${userId}/admin-status`
        });
        
        response = await fetch(`${getApiBaseUrl()}/api/auth/users/${userId}/admin-status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        });
        
        console.log('🔄 Response status:', response.status);
      } else if (action === 'delete') {
        // Handle delete action
        response = await fetch(`${getApiBaseUrl()}/api/auth/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
      } else {
        // Handle other user actions (approve, block, activate, stop)
        response = await fetch(`${getApiBaseUrl()}/api/auth/users/${userId}/${action}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
      }

      if (!response.ok) {
        let serverMessage = '';
        try {
          const errorData = JSON.parse(await response.text());
          serverMessage = errorData.message || '';
        } catch (_) {
          // ignore parse errors
        }
        if (response.status === 401) {
          throw new Error(serverMessage || 'Authentication expired. Please login again.');
        }
        if (response.status === 403) {
          throw new Error(serverMessage || 'Access denied.');
        }
        if (response.status === 404) {
          throw new Error(serverMessage || 'User not found');
        }
        throw new Error(serverMessage || `Request failed (${response.status})`);
      }

      const data = await safeParseJson(response);
      if (!data) {
        showToast('Server response was invalid. Please try again.', 'error');
        fetchUsers();
        return;
      }
      if (data.success) {
        if (action === 'delete') {
          // Remove deleted user from local state
          setUsers(prevUsers => 
            prevUsers.filter(user => user.id !== userId)
          );
          showToast('User deleted successfully', 'success');
        } else {
          // Update the user in the local state
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.id === userId 
                ? { 
                    ...user, 
                    status: action === 'approve' ? 'active' 
                           : action === 'block' ? 'blocked'
                           : action === 'activate' ? 'active'
                           : action === 'stop' ? 'stopped'
                           : user.status,
                    is_admin: action === 'setAdmin' ? true 
                             : action === 'removeAdmin' ? false
                             : user.is_admin,
                    isAdmin: action === 'setAdmin' ? true 
                            : action === 'removeAdmin' ? false
                            : user.isAdmin
                  }
                : user
            )
          );

          const actionMessages = {
            approve: 'User approved successfully',
            block: 'User blocked successfully',
            activate: 'User activated successfully',
            stop: 'User stopped successfully',
            setAdmin: 'User granted admin privileges successfully',
            removeAdmin: 'User admin privileges removed successfully'
          };

          showToast(actionMessages[action] || 'Action completed successfully', 'success');
        }
        
        // Refresh stats
        fetchUserStats();
      } else {
        throw new Error(data.message || `Failed to ${action} user`);
      }
    } catch (err) {
      console.error('Error performing user action:', err);
      showToast(err.message || `Failed to ${action} user. Please try again.`, 'error');
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;

    // Show confirmation for delete action
    if (bulkAction === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to permanently delete ${selectedUsers.length} user(s)? This action cannot be undone.`
      );
      if (!confirmed) {
        setBulkAction('');
        return;
      }
    }

    try {
      let data;
      
      if (bulkAction === 'delete') {
        // Use bulk delete service
        data = await userService.bulkDeleteUsers(selectedUsers);
      } else {
        // Use bulk update service for other actions
        data = await userService.bulkUpdateUsers(selectedUsers, bulkAction);
      }
      
      if (data.success) {
        if (bulkAction === 'delete') {
          // Remove deleted users from local state
          setUsers(prevUsers => 
            prevUsers.filter(user => !selectedUsers.includes(user.id))
          );
          showToast(`${data.deletedCount} user(s) deleted successfully`, 'success');
          
          if (data.failedCount > 0) {
            showToast(`${data.failedCount} user(s) could not be deleted`, 'warning');
          }
        } else {
          // Update users locally for other actions
          setUsers(prevUsers => 
            prevUsers.map(user => 
              selectedUsers.includes(user.id)
                ? { 
                    ...user, 
                    status: bulkAction === 'approve' ? 'active' 
                           : bulkAction === 'block' ? 'blocked'
                           : bulkAction === 'activate' ? 'active'
                           : bulkAction === 'stop' ? 'stopped'
                           : user.status
                  }
                : user
            )
          );
          showToast(`${bulkAction} action completed for ${selectedUsers.length} users`, 'success');
        }

        setSelectedUsers([]);
        setBulkAction('');
        
        // Refresh data
        fetchUsers();
        fetchUserStats();
      } else {
        throw new Error(data.message || `Failed to perform bulk ${bulkAction}`);
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
      showToast(err.message || `Failed to perform bulk ${bulkAction}. Please try again.`, 'error');
    }
  };

  // Handle search
  const handleSearch = useCallback(async (searchQuery) => {
    setSearchTerm(searchQuery);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const handleStatClick = (statId) => {
    const statusMap = {
      total: 'all',
      active: 'active',
      pending: 'pending',
      blocked: 'blocked'
    };
    
    if (statusMap[statId] && statusMap[statId] !== statusFilter) {
      handleStatusFilterChange(statusMap[statId]);
      showToast(`Filtered to show ${statId} users`, 'info');
    }
  };

  // Filter users locally for display
  const filteredUsers = filterUsers(users, searchTerm, statusFilter);

  if (loading && !refreshing) {
    const loadBg = currentThemeName === 'dark' ? '#0f172a' : currentThemeName === 'beige' ? '#f5f5dc' : '#f8fafc';
    return (
      <div 
        className="loading-container"
        style={{ 
          minHeight: '100vh', 
          background: loadBg,
          color: currentThemeName === 'dark' ? 'white' : theme.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div 
          className="loading-content" 
          style={{ 
            textAlign: 'center',
            background: loadBg,
            padding: '32px 48px',
            borderRadius: 16
          }}
        >
          <Loader2 size={48} className="animate-spin" style={{ marginBottom: 16, color: currentThemeName === 'dark' ? '#94a3b8' : '#64748b' }} />
          <div className="loading-title" style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Loading users...</div>
          <div className="loading-subtitle" style={{ fontSize: 14, color: currentThemeName === 'dark' ? '#94a3b8' : theme.textTertiary }}>
            Please wait while we fetch your data
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="user-list-page-container"
      style={{ 
        minHeight: '100vh', 
        background: currentThemeName === 'dark'
          ? '#0f172a'
          : currentThemeName === 'beige'
          ? '#f5f5dc'
          : '#ffffff',
        color: currentThemeName === 'dark' ? 'white' : theme.text,
        padding: '3%',
        position: 'relative',
        overflow: 'hidden'
      }}>
      {/* Background gradient */}
      <div 
        className="background-gradient"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: currentThemeName === 'dark'
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)'
            : currentThemeName === 'beige'
            ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(160, 82, 45, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%)',
          zIndex: 0
        }} />

      <div 
        className="content-wrapper"
        style={{ 
          maxWidth: 1100, 
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}>
        

        {/* Header */}
        <UserListHeader 
          onRefresh={fetchUsers}
          refreshing={refreshing}
          theme={theme}
          currentThemeName={currentThemeName}
        />

        {/* API/load error banner */}
        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              borderRadius: 12,
              background: currentThemeName === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
              border: `1px solid ${currentThemeName === 'dark' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.25)'}`,
              color: currentThemeName === 'dark' ? '#fca5a5' : '#b91c1c',
              fontSize: 14
            }}
          >
            {error}
            <span style={{ marginLeft: 8, opacity: 0.9 }}>
              Ensure backend is running and <code style={{ fontSize: 12 }}>REACT_APP_API_URL</code> points to it (e.g. https://yoursite.com:3001), then rebuild frontend.
            </span>
          </div>
        )}

        {/* Stats Cards */}
        <UserStats 
          users={users} 
          userStats={userStats}
          theme={theme} 
          currentThemeName={currentThemeName}
          onStatClick={handleStatClick}
        />

        {/* Search and Filters */}
        <UserFilters
          searchTerm={searchTerm}
          setSearchTerm={handleSearch}
          statusFilter={statusFilter}
          setStatusFilter={handleStatusFilterChange}
          selectedUsers={selectedUsers}
          bulkAction={bulkAction}
          setBulkAction={setBulkAction}
          onBulkAction={handleBulkAction}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          theme={theme}
          currentThemeName={currentThemeName}
        />

        {/* Desktop Table View */}
        <UserTable
          users={users}
          filteredUsers={filteredUsers}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          onRefresh={fetchUsers}
          onUserAction={handleUserAction}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          theme={theme}
          currentThemeName={currentThemeName}
          loading={loading}
          refreshing={refreshing}
          totalUsers={totalUsers}
          loadError={error}
        />

        {/* Mobile Card View */}
        <UserListMobile
          users={users}
          filteredUsers={filteredUsers}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          onRefresh={fetchUsers}
          onUserAction={handleUserAction}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          theme={theme}
          currentThemeName={currentThemeName}
        />
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          theme={theme}
          currentThemeName={currentThemeName}
        />
      )}
    </div>
  );
}

export default UserListPage; 